<template>
  <div
    class="rs-data-table-v2"
    :class="{ 'border-0': hasNoBorder, 'rounded-0': hasNoRounded }"
  >
    <div
      v-if="!noFilter"
      class="filter-bar align-items-end"
      :class="{ 'rounded-0': hasNoRounded }"
      :style="`background:${filterBgColor}`"
    >
      <div class="filter-bar-left">
        <slot :name="slotMap.filterBeforeSearchSlot" />
      </div>
      <RsFormInput
        v-if="fields.some((v) => v.isSearchable)"
        :id="`${id}_data_table_search`"
        v-model="dataFilters.searchValue"
        type="search"
        :placeholder="searchBoxPlaceholder"
        icon="search"
        :debounce="500"
        :label="searchBoxLabel"
        :style="{ width: searchBoxWidth }"
        class="rs-data-table__filter-bar--search mr-2"
        @input="handleSearchInput"
      />

      <div class="filter-bar-left flex-grow-1 d-flex align-items-end justify-content-start">
        <slot :name="slotMap.filterLeft" />
      </div>

      <div class="filter-bar-right flex-grow-1 d-flex align-items-end justify-content-end">
        <slot :name="slotMap.filterRight" />
      </div>

      <RsButton
        v-show="hasExport"
        variant="dark"
        icon="file-download"
        title="Export to CSV"
        class="filter-bar-export ml-3"
        @click="exportConfig && exportConfig.hasDateRangeModal ? isExportModalOpen = true : handleExport()"
      >
        {{ $ts('dataTable.exportCSV') }}
      </RsButton>

      <DownloadExcel
        :data="dataList"
        :fields="jsonFields"
        style="padding:0,height:0"
        type="csv"
        :escape-csv="simpleExportEscapeCsv"
        :name="`${simpleExportFileName}.csv`"
      >
        <RsButton
          v-show="hasSimpleExport"
          variant="dark"
          icon="file-download"
          title="Export to CSV"
          class="filter-bar-export ml-3"
        >
          Export CSV
        </RsButton>
      </DownloadExcel>
    </div>

    <div
      v-show="showHeader"
      :class="{ header: true, 'is-compact': isCompact }"
      :style="`background:${headerBgColor}`"
    >
      <div
        :class="{
          'row header-row': true,
          'm-0': !isDraggable,
          'mx-1': isDraggable,
          'is-compact-row': isCompact,
        }"
      >
        <div
          v-if="isDraggable && !hideReorderButton"
          class="drag-grip mx-1 align-self-center align-items-center flex-grow-0"
        >
          <button
            v-b-tooltip.hover.right="isReorderMode ? 'Exit reorder mode (Esc)' : 'Reorder'"
            class="reorder-toggle-btn"
            :class="{ 'reorder-toggle-btn--active': isReorderMode }"
            @click="toggleReorderMode"
          >
            <RsIcon icon="solid fa-grip-vertical" />
          </button>
        </div>
        <div
          v-if="showCheckboxes"
          class="col header-cell row m-0 align-self-center align-items-center checkbox flex-grow-0"
        >
          <RsFormCheckbox
            :id="`${id}_checkbox_all`"
            v-model="checkboxAll"
            v-b-tooltip.hover.rightbottom="isEveryCheckboxDisabled ? checkboxDisabledTooltipText : selectAllTooltipText"
            :disabled="isEveryCheckboxDisabled"
            :true-value="true"
            :false-value="false"
            @change="handleAllItemsChecked()"
          />
        </div>
        <div
          v-for="(field, index) in (fields.filter((v) => v.variant !== 'hidden'))"
          v-show="shouldShowHeader(field)"
          :key="index"
          :title="field.display"
          :class="`header-cell row m-0 align-items-center
            ${field.variant}
            ${field.isSortable ? 'sortable' : ''}
            ${field.isCentered ? 'centered' : ''}
            ${getColWidthClass(field)}
            ${field.property.replace(/\./g, '-')}`"
          :style="`${getCellExpandedClass('header', field)}`"
          @click="handleSorting(field)"
        >
          <span
            v-if="field.isSortable"
            :style="`${field.variant === 'status' && field.isSortable ? 'margin-left:10px;' : '' }`"
          >
            <span
              class="table-sort-container mr-1"
            >
              <RsIcon
                :class="`sort-icon--up${isFieldSortActive(field, 'desc') ? '--active' : ''}`"
                weight="fa"
                icon="chevron-up"
              />

              <RsIcon
                :class="`sort-icon--down${isFieldSortActive(field, 'asc') ? '--active' : ''}`"
                weight="fa"
                icon="chevron-down"
              />

            </span>
          </span>
          <span>{{ field.display }}</span>
          <span v-if="field.titleHelperTooltip">
            <RsIcon
              :id="`helperTooltip-${id}-${index}`"
              icon="question-circle"
              class="question-icon"
            />
            <RsTooltip
              :target="`helperTooltip-${id}-${index}`"
              :title="field.titleHelperTooltip"
              position="top"
              :delay="{ show: 200 }"
            />
          </span>
          <span v-if="field.hasClickableInformationIcon">
            <RsIcon
              :id="`information-${id}-${index}`"
              icon="info-circle"
              weight="fas"
              class="header-information-icon"
              @click="$emit(eventsMap.onInformationIconClicked, field)"
            />
          </span>
          <span v-if="field.favouriteFilter">
            <RsFavorite
              v-model="filterFavourites"
              icon="star"
              title="Toggle Star"
              :true-value="true"
              :false-value="false"
              @input="$emit(eventsMap.onFavoriteFilterClicked, filterFavourites)"
            />
          </span>
          <slot v-if="field.displaySlot" :name="field.displaySlot" />
          <div
            v-if="field.variant === 'radio'"
            class="col-12 p-0"
          >
            <RsFormRadio
              :id="`${id}_data_table_radio_${(field.property.replace('?custom', ''))}_${index}_all`"
              v-model="radioAll"
              :value="field.trueValue"
              label="All"
              class="radio-all"
              @change="handleRadioAllClicked(field)"
            />
          </div>
        </div>

        <div
          v-if="hasDetailsView"
          class="col header-cell align-self-center flex-grow-0 details-view"
        />

        <div
          v-if="hasDropdown"
          class="col header-cell align-self-center flex-grow-0"
        />
      </div>
    </div>

    <div
      v-if="isLoading"
      class="content loading"
      :style="`background: ${bodyBgColor}`"
    >
      <div class="spin">
        <RsSpin class="mt-5" />
      </div>
    </div>

    <div
      v-else-if="!isLoading && dataList.length === 0"
      class="content no-data"
      :style="`background: ${bodyBgColor}`"
    >
      <RsNoData
        v-if="!noDataMessageUseHelpText"
        :message="noDataMessage"
      />

      <div
        v-else
        class="mt-4 d-flex justify-content-around no-data-container-height"
      >
        <RsBanner
          class="w-75"
          type="help-text-primary"
          :has-action-btn="hasBannerButton"
          :action-button-text="bannerButtonText"
          custom-btn-text-color="FFFFFF"
          :message="noDataMessage"
          @on-action="handleBannerAction()"
        />
      </div>
    </div>

    <div
      v-else
      class="content"
      :style="`background: ${bodyBgColor}`"
    >
      <slot :name="slotMap.bulkEdit" />
      <Component
        :is="autoHeight ? 'div' : 'OverlayScrollbars'"
        ref="scrollbar"
        :options="{ sizeAutoCapable: false, callbacks: { onScrollStop: handleScrollStop } }"
      >
        <Component
          :is="isDraggable ? `RsDragndrop` : `div`"
          non-drag-area-selector=".no-drag"
          @drop="(data) => $emit('drop', data)"
        >
          <Component
            :is="isDraggable ? `RsDragger` : `div`"
            v-for="(item, itemIndex) in scrollItemsWithSkip"
            :key="`${item.id}_${itemIndex}`"
            class="row align-items-center content-row"
            :class="
              `${ itemIndex === scrollItemsWithSkip.length - 1 && boldLastRow ? 'font-weight-bold' : '' }
              ${getDynamicRowClasses(item, itemIndex)}`"
            :style="`background: ${hasDetailsView && isIndexSelected(itemIndex) ? '#dfe1f8' : rowBgColor};`"
          >
            <div
              v-if="isDraggable"
              class="drag-grip mx-1 align-self-center align-items-center flex-grow-0"
            >
              <RsIcon icon="solid fa-grip-vertical" />
            </div>
            <div
              v-if="showCheckboxes"
              class="col flex-grow-0 py-0 content-cell checkbox"
            >
              <RsFormCheckbox
                :id="`${id}_data_table_row_check_${itemIndex}_${item.id}`"
                v-b-tooltip.hover.rightbottom="isItemDisabled(item) && checkboxDisabledTooltipText"
                :model-value="checkedItems.some((val) => val[checkboxBoundDataProperty] === item[checkboxBoundDataProperty])"
                :true-value="true"
                :false-value="false"
                :disabled="isItemDisabled(item)"
                @change="handleCheckboxChange($event, item)"
              />
            </div>
            <div
              v-for="(field, index) in (fields.filter((v) => v.variant !== 'hidden' && shouldShowField(v, item)))"
              :id="`${id}_data_table_cell_${field.variant}_col_${index}_row_${itemIndex}`"
              :key="`${index}_cell`"
              :colspan="field.colspan || 0"
              :class="`content-cell ${typeof field.variant === 'function' ? field.variant(item) : field.variant} ${field.isCentered ? 'centered' : ''} ${getColWidthClass(field)}`"
              @click="handleRowSelect(item, field, itemIndex)"
            >
              <div class="text-truncate">
                <!-- VARIANT - FAVORITE -->
                <RsFavorite
                  v-if="field.variant === 'favorite' && showFavorite === false"
                  v-model="item[field.property.replace('?custom', '')]"
                  class="flex-grow-0"
                  :true-value="field.trueValue"
                  :false-value="field.falseValue"
                  @input="$emit(eventsMap.onFavoriteClicked, item)"
                />
                <RsFavorite
                  v-if="field.variant === 'favorite' && showFavorite === true"
                  :value="item[field.property.replace('?custom', '')]"
                  icon="star"
                  class="flex-grow-0"
                  title="Toggle Star"
                  :true-value="field.trueValue"
                  :false-value="field.falseValue"
                  @input="$emit(eventsMap.onFavoriteClicked, item, $event)"
                />

                <!-- VARIANT - SWITCH -->
                <RsFormSwitch
                  v-else-if="field.variant === 'switch'"
                  :id="`${id}_data_table_switch_${(field.property.replace('?custom', ''))}_${itemIndex}`"
                  v-model="item[field.property.replace('?custom', '')]"
                  :true-value="field.trueValue"
                  :false-value="field.falseValue"
                  :disabled="field.disabled"
                  @change="handleSwitchChanged(field, item)"
                />

                <!-- VARIANT - CATEGORY -->
                <div
                  v-else-if="field.variant === 'category'"
                  class="d-flex justify-content-center"
                >
                  <RsPill
                    :variant="((field.variantOptions || []).find((v) => v.value === getPropertyValue(field, item)) || {}).subVariant"
                    :title="getPropertyValue(field, item)"
                  />
                </div>

                <!-- VARIANT - STATUS -->
                <div
                  v-else-if="field.variant === 'status'"
                  :title="!field.hideTitle ? stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)) : false"
                  class="text-truncate"
                >
                  <RsStatus
                    :variant="getPropertyValue(field, item)"
                    :show-icon="field.statusIcon"
                  />
                </div>

                <!-- VARIANT - CLICKABLE -->
                <div v-else-if="field.variant === 'clickable' || (typeof field.variant === 'function' && field.variant(item) === 'clickable')">
                  <Component
                    :is="field.useRouterLink ? 'router-link' : 'a'"
                    :id="`${id}_data_table_${(typeof field.variant === 'function' ? field.variant(item) : field.variant)}_col_${index}_row_${itemIndex}_anchor`"
                    :role="field.dynamicHref || field.href ? 'link' : 'button'"
                    class="d-block clickable-text"
                    :title="!field.hideTitle ? stripHTMLTags(`${getPropertyValue(field, item)}`) : false"
                    :to="field.dynamicHref || field.href ? getHref(item, field) : ''"
                    :href="field.dynamicHref || field.href ? getHref(item, field) : false"
                    @click="handleItemClicked(item, field, itemIndex)"
                  >
                    <div
                      class="text-truncate"
                      v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`)"
                    />

                    <div
                      v-if="field.hasSubText"
                      class="sub-text text-truncate"
                    >
                      {{ $options.filters.rsDecodeHtml(`${getSubTextValue(field, item)}`) }}
                    </div>
                  </Component>
                </div>

                <!-- VARIANT - AUDIO -->
                <div v-else-if="field.variant === 'audio'">
                  <RsButton
                    v-if="checkValue(field, item)"
                    class="rs-button--rounded rs-button--light play-button"
                    icon="play"
                    :disabled="field.disabled"
                    @click="$emit(eventsMap.onAudioClicked, item, itemIndex)"
                  >
                    {{ getPropertyValue(field, item) }}
                  </RsButton>
                  <div
                    v-else
                    class="ml-3"
                  >
                    {{ getPropertyValue(field, item) }}
                  </div>
                </div>

                <!-- VARIANT - DUPLICATE -->
                <div v-else-if="field.variant === 'duplicate'">
                  <button
                    class="clickable-text"
                    @click="handleItemClicked(item, field, index)"
                  >
                    <RsTooltip
                      :target="`${id}_data_table_duplicate_${itemIndex}_${index}`"
                      title="Duplicate Unit"
                      position="left"
                      :delay="{ show: 200 }"
                    />
                    <RsIcon
                      :id="`${id}_data_table_duplicate_${itemIndex}_${index}`"
                      weight="far"
                      icon="clone"
                    />
                  </button>
                </div>

                <!-- VARIANT - DELETE -->
                <div v-else-if="field.variant === 'delete'">
                  <button
                    class="clickable-text"
                    @click="handleDeleteClicked(item, index)"
                  >
                    <RsIcon
                      :id="`${id}_data_table_delete_${itemIndex}_${index}`"
                      class="trashIcon"
                      weight="far"
                      icon="trash-alt"
                      title="Delete"
                    />
                  </button>
                </div>

                <!-- VARIANT - DATE -->
                <div
                  v-else-if="field.variant === 'date'"
                  class="d-block text-truncate"
                >
                  {{ getPropertyValue(field, item) | rsDateTime }}
                </div>

                <!-- VARIANT - RADIO -->
                <div
                  v-else-if="field.variant === 'radio'"
                  class="d-flex w-100 align-items-center justify-content-center"
                >
                  <RsFormRadio
                    :id="`${id}_data_table_radio_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
                    :model-value="field.radioModelValue(item)"
                    :value="field.trueValue"
                    :disabled="field.disabled"
                    @change="handleRadioClicked(field, item, itemIndex)"
                  />
                </div>

                <!-- VARIANT - BUTTON -->
                <RsButton
                  v-else-if="field.variant === 'button'"
                  :id="`${id}_data_table_action_button_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
                  :variant="field.variantColour"
                  :icon="field.variantIcon"
                  style="height: 32px;"
                  @click="$emit(`on-${field.property}-clicked`, item)"
                >
                  {{ field.variantDisplayValue }}
                </RsButton>

                <!-- VARIANT - INPUT -->
                <!-- class="form-control" -->
                <input
                  v-else-if="field.variant === 'input'"
                  :id="`${id}_data_table_input_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
                  :style="`${getCellExpandedClass('content', field)}`"
                  :class="`form-control ${getPropertyClasses(field, item).join(' ')}`"
                  :value="getPropertyValue(field, item)"
                  :type="field.inputType"
                  :disabled="field.disabled"
                  @change="onInputChanged(item, itemIndex, field, $event.target.value)"
                  @focus="toggleExpandableMode(field, getPropertyValue(field, item).length + 2)"
                  @blur="setColDefaultMode(field)"
                >

                <!-- VARIANT - SELECT -->
                <!-- class="form-control" -->
                <select
                  v-else-if="field.variant === 'select'"
                  :id="`${id}_data_table_select_${(field.property.replace('?custom', ''))}_${itemIndex}_${index}`"
                  :class="`form-control ${getPropertyClasses(field, item).join(' ')}`"
                  :value="getPropertyValue(field, item)"
                  :disabled="field.disabled"
                  @change="$emit(eventsMap.onSelectChanged, item, itemIndex, field, index, $event.target.value, previousSelect)"
                  @focus="updatePreviousSelect($event.target.value)"
                >
                  <option
                    v-for="opt in field.selectOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.text }}
                  </option>
                </select>

                <!-- VARIANT - TEXT w/ ICON -->
                <div
                  v-else-if="field.variant === 'text-icon'"
                  class="text-icon"
                >
                  <RsIcon
                    weight="fas"
                    :icon="field.dynamicIcon(item)"
                  />
                  <div
                    :title="!field.hideTitle ? stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)) : false"
                    :class="{ 'd-block text-truncate': $options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`).length > 25 }"
                    v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`)"
                  />
                </div>

                <!-- VARIANT - PRICE -->
                <div
                  v-else-if="field.variant === 'price'"
                  class="d-block text-truncate w-100"
                  :style="{ maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
                >
                  $ {{ Number.parseFloat(getPropertyValue(field, item) || 0).toFixed(2) }}
                </div>

                <!-- VARIANT - SLOT -->
                <div
                  v-else-if="field.variant === 'slot'"
                  :style="{ width: '100%', maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
                  tabindex="0"
                  @focusin="field.isExpandableCol && setExpandableMode(field, getPropertyValue(field, item).length + 2)"
                  @focusout="setColDefaultMode(field)"
                >
                  <slot
                    :item="item"
                    :index="itemIndex"
                    :name="field.property.replace(/\?custom|\.value/gi, '').replace(/\./g, '_')"
                    :field="field"
                    :dynamic-styles="`${getCellExpandedClass('content', field)}`"
                  />
                </div>

                <!-- VARIANT - TEXT (DEFAULT) -->
                <div
                  v-else
                  class="d-block text-truncate w-100"
                  :style="{ maxWidth: (field.colspan || 0 > 1) ? '100%' : '' }"
                >
                  <div
                    :title="!field.hideTitle ? stripHTMLTags($options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)) : false"
                    class="d-block text-truncate"
                    v-html="$options.filters.rsDecodeHtml(`${getPropertyValue(field, item)}`, true)"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="hasDetailsView && !item.unexpandable"
              class="col flex-grow-0 content-cell details-view"
            >
              <button
                :id="`${id}_data_table_details_view_${itemIndex}`"
                class="rs-text-dark"
                @click="handleDetailsViewToggle(itemIndex, item)"
              >
                <i
                  v-if="detailsViewType === 'accordion'"
                  class="fas fa-chevron-down"
                  :class="{ 'fa-rotate-180': isIndexSelected(itemIndex) }"
                />
                <i
                  v-else
                  :class="{ 'btn-details-view-toggle': true, 'fas fa-plus': !isIndexSelected(itemIndex), 'fas fa-minus': isIndexSelected(itemIndex) }"
                />
              </button>
            </div>

            <div class="remove-btn">
              <slot
                name="removebtn"
                :item="item"
              />
            </div>

            <div
              v-if="hasDropdown"
              class="col flex-grow-0 content-cell dropdown"
            >
              <RsDropdownActionMenu
                :id="`${id}_data_table_dropdown_${itemIndex}`"
                ref="dropdownParent"
                icon="ellipsis-v"
                :disabled="getDropdownOptions(item).length === 0"
                right
                action-menu
                :options="getDropdownOptions(item)"
                @dropdown-opened="(dropdown) => hideOtherDropdowns(dropdown)"
              />
            </div>

            <div
              v-if="isIndexSelected(itemIndex)"
              :id="`${id}_details_view_${itemIndex}`"
              ref="detailsView"
              class="col-12 p-0 details-view-container"
              :style="`cursor: default;`"
            >
              <slot
                :item="item"
                :index="itemIndex"
                :name="slotMap.details"
              />
            </div>
          </Component>
          <div
            v-if="showMoreButton && dataList.length > showMoreLimit && !isShowMoreButtonClicked"
            class="content-cell clickable"
          >
            <div
              id="show-more-button"
              class="content-row clickable-text d-flex align-items-center justify-content-end mr-5"
              :style="`background: ${hasDetailsView ? '#dfe1f8' : rowBgColor};`"
              @click="isShowMoreButtonClicked = true"
            >
              Show More
            </div>
          </div>
        </Component>
      </Component>
    </div>

    <div
      v-if="hasFooter"
      class="footer"
      :class="{ 'rounded-0': hasNoRounded }"
      :style="`background: ${footerBgColor}`"
    >
      <RsPaginationBar
        v-if="pagination && !isLoading"
        v-model="pagination"
        @input="handlePaginationEmit"
      />
    </div>

    <RsModal
      v-model="isExportModalOpen"
      :title="$ts('dataTable.modalExport.pickDateRange')"
      size="md"
      lazy
      :button-text="$ts('actions.export')"
      @submit="isDateRangeValid && handleExport();"
    >
      <div>
        <RsDateRange
          id="dt_date_range"
          :date-from="exportDateMin"
          :date-to="exportDateMax"
          @on-min-date-change="(exportDateMin) = $event"
          @on-max-date-change="(exportDateMax) = $event"
        />
      </div>
    </RsModal>
  </div>
</template>

<script lang="ts">
import { GreaterThan, INestedWhereCommand, IQueryCommandOptions, IWhereCommand, LessThan } from '@llws/dynamic-router-utils';
import dayjs from 'dayjs';
import { ScrollEventCallback } from 'overlayscrollbars';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-vue';
import { defineComponent, PropType } from 'vue';
import RsDropdownActionMenu from '@/components/_global/RsDropdownActionMenu.vue';
import { TDataTableVariant } from '@/shared/types/t-data-table-variant';
import {
  IBaseFieldConfig,
  IDataTableCustomFilter,
  IDataTableEmitPayload,
  IDataTableExportConfig,
  IDataTableFieldConfig,
  IDataTableRouteQuery,
  IDropdownOptions,
  IExportConfig,
  IGenericObject,
  IMetaResponse,
  IQueryStringFilterOptions,
  pageRange,
} from '@/shared';
import i18n from '@/i18n';

export interface IRowCustomStyles { [key: string]: number | string | Object, customClasses: string }

const eventsMap: Record<string, string> = {
  onFiltersChanged: 'on-filters-changed',
  onPaginationChanged: 'on-pagination-changed',
  onItemClicked: 'on-item-clicked',
  onDeleteClicked: 'on-delete-clicked',
  onCheckedItemsChanged: 'on-checked-items-changed',
  onSortingChanged: 'on-sorting-changed',
  onExportClicked: 'on-export-clicked',
  onDetailsViewOpened: 'on-details-view-opened',
  onDetailsViewClosed: 'on-details-view-closed',
  onRowSelected: 'on-row-selected',
  onFavoriteClicked: 'on-favorite-clicked',
  onFavoriteFilterClicked: 'on-favorite-filter-clicked',
  onActionButtonPressed: 'on-action-button-pressed',
  onAudioClicked: 'on-audio-clicked',
  onRadioChanged: 'on-radio-changed',
  onRadioAllChanged: 'on-radio-all-changed',
  onSelectChanged: 'on-select-changed',
  onInputChanged: 'on-input-changed',
  onInformationIconClicked: 'on-information-icon-clicked',
  onSearch: 'on-search',
};

const slotMap: Record<string, string> = {
  filterBeforeSearchSlot: 'filterBeforeSearchSlot',
  filterLeft: 'filterLeft',
  filterRight: 'filterRight',
  bulkEdit: 'bulkEdit',
  details: 'details',
};

const defaultFilters: IDataTableRouteQuery = {
  searchValue: '',
  limit: '20',
  page: '1',
  orderDir: 'asc',
};

const rowSelectableVariants: TDataTableVariant[] = [
  'text',
  'category',
  'status',
  'date',
  'text-icon',
  'slot',
];

const now = dayjs();

export default defineComponent({

  name: 'RsDataTableV2',

  props: {
    id: {
      type: String,
      required: true,
    },

    showHeader: {
      type: Boolean,
      default: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    filterOnInit: {
      type: Boolean,
      default: true,
    },

    noFilter: {
      type: Boolean,
      default: false,
    },

    hasFooter: {
      type: Boolean,
      default: true,
    },

    hasSubText: {
      type: Boolean,
      default: false,
    },

    showCheckboxes: {
      type: Boolean,
      default: false,
    },

    showFavorite: {
      type: Boolean,
      default: false,
    },

    checkboxBoundDataProperty: {
      type: String,
      default: 'id',
    },

    exportConfig: {
      type: Object as PropType<IDataTableExportConfig>,
      default: null,
    },

    fields: {
      type: Array as PropType<IDataTableFieldConfig<any>[]>,
      default: () => [],
    },

    dataList: {
      type: Array as PropType<Record<string, any>[]>,
      default: () => [],
    },

    hasSimpleExport: {
      type: Boolean,
      default: false,
    },

    boldLastRow: {
      type: Boolean,
      default: false,
    },

    simpleExportFileName: {
      type: String,
      default: 'export',
    },

    simpleExportEscapeCsv: {
      type: Boolean,
      default: false,
    },

    dataMeta: {
      type: Object as PropType<IMetaResponse>,
      default: null,
    },

    dropdownItems: {
      type: Array as PropType<IDropdownOptions[]>,
      default: () => [],
    },

    rowSelectable: {
      type: Boolean,
      default: false,
    },

    rowSelectOpensDetailsView: {
      type: Boolean,
      default: false,
    },

    noDataMessage: {
      type: String,
      default: null,
    },

    noDataMessageUseHelpText: {
      type: Boolean,
      default: false,
    },

    hasBannerButton: {
      type: Boolean,
      default: false,
    },

    bannerButtonText: {
      type: String,
      default: 'Action',
    },

    searchBoxPlaceholder: {
      type: String,
      default: '',
    },

    selectAllTooltipText: {
      type: String,
      default: 'Select All',
    },

    searchBoxWidth: {
      type: String,
      default: '',
    },

    searchBoxLabel: {
      type: String,
      default: () => i18n.t('actions.search'),
    },

    hasExport: {
      type: Boolean,
      default: false,
    },

    height: {
      type: Number,
      default: null,
    },

    autoHeight: {
      type: Boolean,
      default: false,
    },

    filterBgColor: {
      type: String,
      default: '#fff',
    },

    headerBgColor: {
      type: String,
      default: '#f8f8f8',
    },

    bodyBgColor: {
      type: String,
      default: '#fff',
    },

    rowBgColor: {
      type: String,
      default: '#fff',
    },

    footerBgColor: {
      type: String,
      default: '#fff',
    },

    hasDetailsView: {
      type: Boolean,
      default: false,
    },

    detailsViewType: {
      type: String,
      validator: (val: string) => ['accordion', 'toggle'].includes(val),
      default: 'accordion',
    },

    hasNoBorder: {
      type: Boolean,
      default: false,
    },

    hasNoRounded: {
      type: Boolean,
      default: false,
    },

    logAvailableEvents: {
      type: Boolean,
      default: false,
    },

    selectedItemIndex: {
      type: Number,
      default: null,
    },

    openDetailsByIndex: {
      type: Number,
      default: null,
    },

    isCompact: {
      type: Boolean,
      default: false,
    },

    longTextLimit: {
      type: Number,
      default: 25,
    },
    onScrollBottom: {
      type: Function as PropType<(payload: IQueryStringFilterOptions) => void | undefined>,
    },
    scrollbarStepSize: {
      type: Number,
      default: null,
    },
    rowCustomStyles: {
      type: [Object, Array] as PropType<IRowCustomStyles | IRowCustomStyles[]>,
      default: null,
    },
    usePreciseWatch: {
      type: Boolean,
      default: false,
    },
    isDraggable: {
      type: Boolean,
      default: false,
    },
    hideReorderButton: {
      type: Boolean,
      default: false,
    },
    exitReorderTrigger: {
      type: Number,
      default: 0,
    },
    disableCheckboxFunction: {
      type: Function as PropType<((item: IGenericObject) => boolean) | undefined>,
    },
    checkboxDisabledTooltipText: {
      type: String,
      default: '',
    },
    showMoreButton: {
      type: Boolean,
      default: false,
    },
    showMoreLimit: {
      type: Number,
      default: 5,
    },
    drAlias: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      eventsMap,
      slotMap,
      radioAll: null,
      skip: 1,
      filterFavourites: false,
      checkboxAll: false,
      showOptions: false,
      isInited: false,
      hasAllItemsChecked: false,
      isExportModalOpen: false,
      exportDateMin: dayjs(`${now.year()}-${now.month() + 1}-01`).format('YYYY-MM-DD'),
      exportDateMax: now.format('YYYY-MM-DD'),
      checkedItems: [] as Array<Record<string, number | string | Object | Date>>,
      dataFilters: {
        ...defaultFilters,
        ...{
          orderBy:
            this.fields.find((f: IDataTableFieldConfig) => f.isDefaultSortValue)
              ?.property?.replace('?custom', '') || '',
          orderDir:
            this.fields.find((f: IDataTableFieldConfig) => f.isDefaultSortValue)
              ?.defaultSortValueDir || 'asc',
        },
        ...(this.$route && this.$route.query && this.$route.query),
      } as IDataTableRouteQuery,
      pagination: this.dataMeta ? pageRange(this.dataMeta) : undefined,
      oldSearchValue:
        this.$route && this.$route.query && this.$route.query.searchValue
          ? this.$route.query.searchValue
          : '',
      oldLimit:
        this.$route && this.$route.query && this.$route.query.limit
          ? this.$route.query.limit
          : defaultFilters.limit,
      emitPayload: {} as IDataTableEmitPayload<IDataTableRouteQuery, IQueryCommandOptions>,
      customFilters: [] as IDataTableCustomFilter<IWhereCommand>[],
      detailsViewIndexes: [] as Array<number>,
      isReorderMode: false as boolean,
      detailsViewHeight: 0,
      previousSelect: '',
      expandableColumnMaxWidth: 40 as number,
      clickedExpandableCols: {} as { [key: string]: number },
      isShowMoreButtonClicked: false,
      escHandler: null as ((e: KeyboardEvent) => void) | null,
    };
  },

  computed: {
    scrollbarEl(): InstanceType<typeof OverlayScrollbarsComponent> {
      return this.$refs.scrollbar as unknown as InstanceType<typeof OverlayScrollbarsComponent>;
    },

    scrollItemsWithSkip(): Record<string, any>[] {
      if (this.showMoreButton && !this.isShowMoreButtonClicked) {
        return this.dataList.slice(0, this.showMoreLimit);
      }
      if (this.scrollbarStepSize) {
        return this.dataList.slice(0, this.scrollbarStepSize * this.skip);
      }
      return this.dataList;
    },
    tableClasses(): Record<string, string> {
      return {};
    },
    jsonFields(): Record<string, string> {
      const values: any = {};
      this.fields.forEach((i: IDataTableFieldConfig) => {
        if (!i.hideFromExport) {
          values[i.display] = i.property.replace('?custom', '');
        }
      });
      return values;
    },

    colspan(): number {
      return (
        this.fields.length
        + (this.dropdownItems.length > 0 ? 1 : 0)
        + (this.showCheckboxes ? 1 : 0)
      );
    },

    getStyle(): Record<string, string> {
      if (this.autoHeight) {
        return { height: 'auto' };
      }

      if (!this.height) return {};

      return {
        height: `${this.height}px`,
      };
    },

    hasFilterRightSlot(): boolean {
      return !!this.$slots.filterRight;
    },

    hasFilterBeforeSearchSlot(): boolean {
      return !!this.$slots.filterBeforeSearchSlot;
    },

    hasFilterLeftSlot(): boolean {
      return !!this.$slots.filterLeft;
    },

    hasBulkEditSlot(): boolean {
      return !!this.$slots.bulkEdit;
    },

    hasFilterShelfSlot(): boolean {
      return !!this.$slots.filterShelf;
    },

    hasDropdown(): boolean {
      return this.fields.some((f: IDataTableFieldConfig) => f.variant === 'dropdown') || this.dropdownItems.length > 0;
    },

    checkValue(): (field: IDataTableFieldConfig, item: Record<string, any>) => boolean {
      return (field: IDataTableFieldConfig, item: Record<string, any>) => this.getPropertyValue(field, item) !== 'N/A';
    },

    isDateRangeValid(): boolean {
      if (this.exportConfig?.hasDateRangeModal) {
        return !dayjs(this.exportDateMax).isBefore(dayjs(this.exportDateMin));
      }

      return true;
    },

    isEveryCheckboxDisabled(): boolean {
      return this.dataList.every(this.isItemDisabled);
    },
  },

  watch: {
    pagination() {
      this.handlePaginationChanged();
    },

    openDetailsByIndex() {
      this.triggerDetailsOpen();
    },

    dataFilters: {
      immediate: true,
      deep: true,
      handler() {
        this.handleDataFiltersChanged();
      },
    },

    emitPayload: {
      deep: true,
      immediate: true,
      handler(
        newValue: IDataTableEmitPayload,
        oldValue: IDataTableEmitPayload,
      ) {
        if (this.filterOnInit || this.isInited) {
          if (this.usePreciseWatch) {
            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
              this.$emit(eventsMap.onFiltersChanged, this.emitPayload);
            }
          } else {
            this.$emit(eventsMap.onFiltersChanged, this.emitPayload);
          }
        }
      },
    },

    dataMeta(newVal, oldVal) {
      if (this.usePreciseWatch) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          this.pagination = pageRange(this.dataMeta);
        }
      } else {
        this.pagination = pageRange(this.dataMeta);
      }
    },

    dataList: {
      immediate: true,
      deep: true,
      handler() {
        this.setRadioAllValue();
      },
    },

    exitReorderTrigger() {
      this.isReorderMode = false;
    },
  },

  mounted() {
    if (this.logAvailableEvents) {
      // eslint-disable-next-line no-console
      console.log('Available events', Object.keys(eventsMap).map((e) => `@${eventsMap[e]}`));

      // eslint-disable-next-line no-console
      console.log('Available slots', Object.keys(slotMap).map((s) => `<template #${slotMap[s]}></template>`));
    }

    // This allows exiting reordering mode by clikcing the 'Esc' key
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.isReorderMode) this.isReorderMode = false;
    };
    window.addEventListener('keydown', this.escHandler);
  },

  beforeDestroy() {
    if (this.escHandler) window.removeEventListener('keydown', this.escHandler);
  },

  methods: {
    handleSearchInput(val: string): void {
      // Removes common special characters that break URLs (%, &, #, ?, =, +, /, ,)
      this.dataFilters.searchValue = val ? val.replace(/[%&#?=+/,]/g, '') : '';
      this.handleSearch(this.dataFilters.searchValue);
      this.handleDataFiltersChanged();
    },

    isFieldSortActive(field: IDataTableFieldConfig, sortType: 'asc' | 'desc') {
      return this.dataFilters.orderBy === field.property.split('?')[0] && this.dataFilters.orderDir === sortType;
    },

    changefilterFavourites() {
      this.filterFavourites = !this.filterFavourites;
    },
    /**
     * Handles the scroll stop event to trigger pagination when nearing the bottom
     * @param {ScrollEventCallback} e - The scroll event callback object
    */
    handleScrollStop(e: ScrollEventCallback) {
      const scrollInfo = this.scrollbarEl?.osInstance()?.scroll();
      if (
        (scrollInfo?.max?.y ?? 0) - (scrollInfo?.position?.y ?? 0) <= 400
        && (this.skip * this.scrollbarStepSize) <= this.dataList.length
      ) {
        this.skip += 1;

        if (this.onScrollBottom) {
          this.onScrollBottom({ page: this.skip, limit: this.scrollbarStepSize });
        }
      }
    },
    /**
     * Generates a CSS style string for expanded cell width based on field and cell type
     * @param {'header' | 'content'} cell - The type of cell ('header' or 'content')
     * @param {IDataTableFieldConfig} field - The field configuration object
     * @returns {string} Returns a CSS style string for width or an empty string if not expanded
    */
    getCellExpandedClass(cell: 'header' | 'content', field: IDataTableFieldConfig): string {
      if (this.clickedExpandableCols[field.property]) {
        const expandedWidth = cell === 'header'
          ? `${(this.clickedExpandableCols[field.property] * 10 + 22)}px`
          : `${this.clickedExpandableCols[field.property] * 10}px`;
        return `width: ${expandedWidth}; max-width: ${expandedWidth}`;
      }
      return '';
    },

    setExpandableMode(field: IDataTableFieldConfig, valLength: number): void {
      const key = `${field.property}`;
      const width = valLength > this.expandableColumnMaxWidth ? valLength : this.expandableColumnMaxWidth;
      this.$set(this.clickedExpandableCols, `${key}`, width);
    },

    setColDefaultMode(field: IDataTableFieldConfig): void {
      const key = `${field.property}`;
      this.$delete(this.clickedExpandableCols, `${key}`);
    },

    toggleExpandableMode(field: IDataTableFieldConfig, valLength: number): void {
      const key = `${field.property}`;
      if (field.isExpandableCol && !this.clickedExpandableCols[key]) {
        this.setExpandableMode(field, valLength);
      } else if (field.isExpandableCol && this.clickedExpandableCols[key]) {
        this.setColDefaultMode(field);
      }
    },
    /**
     * Generates CSS classes for a column based on its expandable state and configuration
     * @param {IDataTableFieldConfig} field - The field configuration object
     * @returns {string} Returns a string of CSS classes for the column
    */
    getColWidthClass(field: IDataTableFieldConfig): string {
      let colClasses: string = '';
      if (field.isExpandableCol && this.clickedExpandableCols[field.property]) {
        colClasses = '';
      } else if ((!field.isExpandableCol && field.colClass) && !this.clickedExpandableCols[field.property]) {
        colClasses = `${field.colClass(field)} col`;
      } else {
        colClasses = 'col';
      }
      return colClasses;
    },

    getDynamicRowClasses(item: Record<string, any>, itemIndex: number): string {
      let dynamicClasses: string = '';
      if (this.rowSelectable && !this.detailsViewIndexes.includes(itemIndex)) {
        dynamicClasses += ' row-select ';
      }
      if (this.rowSelectable && this.rowSelectOpensDetailsView) {
        dynamicClasses += ' cursor-pointer ';
      }
      if (this.selectedItemIndex !== null && itemIndex !== this.selectedItemIndex) {
        dynamicClasses += ' row-unselected ';
      }
      if (this.rowCustomStyles) {
        dynamicClasses += ` ${this.generateCustomRowStyles(item)} `;
      } else if (this.bodyBgColor) {
        dynamicClasses += ` ${{ background: this.bodyBgColor }} `;
      }
      if (this.isCompact) {
        dynamicClasses += ` compact `;
      }
      if (this.isDraggable) {
        dynamicClasses += ` draggable-row m-1 `;
        if (this.isReorderMode) dynamicClasses += ' reorder-mode ';
        else dynamicClasses += ' no-drag ';
      } else {
        dynamicClasses += ` m-0 `;
      }
      return dynamicClasses;
    },

    /**
     * Gets Custom Classes from Row Custom Styles
     * Functionality abstracted from generateCustomRowStyles to support Arrays
     * @param item
     * @param rowCustomStyles
     */
    getCustomClassesForRow(item: Record<string, any>, rowCustomStyles: IRowCustomStyles): string {
      const conditionKeys = Object.keys(rowCustomStyles);
      let conditionsPassed: boolean = false;
      let customRowClasses: string = '';

      conditionsPassed = conditionKeys.every((key): boolean => {
        let areEqual: boolean = false;
        if (key === 'customClasses') {
          areEqual = true;
        } else {
          areEqual = item[key] === rowCustomStyles[key];
        }
        return areEqual;
      });

      if (conditionsPassed) customRowClasses = rowCustomStyles.customClasses;
      return customRowClasses;
    },

    generateCustomRowStyles(item: Record<string, any>): string {
      let customRowClasses: string = '';

      if (Array.isArray(this.rowCustomStyles)) {
        this.rowCustomStyles.forEach((rowCustomStyle) => {
          customRowClasses += this.getCustomClassesForRow(item, rowCustomStyle);
        });
      } else customRowClasses = this.getCustomClassesForRow(item, this.rowCustomStyles);

      return customRowClasses;
    },

    displayTooltip(tip: string): Element {
      const tooltip = document.createElement('i');

      tooltip.innerHTML = `
        <i class="fas fa-question-circle">${tip}</i>
      `;
      return tooltip;
    },
    /**
     * Removes HTML tags from a given string.
     * @param {string} html - The input string containing HTML tags
     * @returns {string} The input string with all HTML tags removed
    */
    stripHTMLTags(html: string): string {
      return `${html}`.replace(/<[^>]*>|/g, '');
    },

    handleLongText(html: string): string {
      const text = this.stripHTMLTags(html);
      return (text.length > this.longTextLimit) ? `${text.substring(0, this.longTextLimit)}...` : text;
    },
    /**
     * Scrolls the content to the bottom with an animation
    */
    goToBottom(): void {
      const x = this.$refs.scrollbar as any;
      const osInstance = x.osInstance();
      osInstance.scrollStop().scroll({
        y: osInstance.scroll().max.y,
      }, 1000, 'easeOutQuad');
    },

    removeTableFilters() {
      this.filterFavourites = false;
      this.dataFilters.searchValue = '';
    },
    /**
     * Sets the value of radioAll based on the uniformity of radio field values in the data list
    */
    setRadioAllValue(): void {
      this.radioAll = null;
      (this.fields as IDataTableFieldConfig[]).forEach((f) => {
        if (f.variant === 'radio') {
          const count = this.dataList.filter((d: Record<string, any>) => (
            typeof f.trueValue === 'object'
              ? JSON.stringify(f.radioModelValue!(d) || {}) === JSON.stringify(f.trueValue || {})
              : f.radioModelValue!(d) === f.trueValue
          )).length;

          if (count > 0 && count === this.dataList.length) {
            this.radioAll = f.trueValue;
          }
        }
      });
    },

    shouldShowField(field: IDataTableFieldConfig, item: Record<string, any>): boolean {
      if (field.dynamicIsHidden) {
        return !field.dynamicIsHidden(item);
      }

      if (field.variant === 'dropdown') {
        return false;
      }

      return true;
    },
    /**
     * Determines if a field's header should be displayed based on the data list
     * @param {IDataTableFieldConfig} field - The field configuration object
     * @returns {boolean} Returns `true` if the header should be shown, `false` otherwise
    */
    shouldShowHeader(field: IDataTableFieldConfig): boolean {
      if (field.dynamicIsHidden) {
        const hiddenFields = this.dataList.filter((v: Record<string, any>) => !this.shouldShowField(field, v));
        return hiddenFields.length !== this.dataList.length;
      }

      if (field.variant === 'dropdown') {
        return false;
      }

      return true;
    },

    getPropertyClasses(field: IDataTableFieldConfig, item: Record<string, any>) {
      const classes = (
        typeof field.inputClasses === 'function'
          ? field.inputClasses(item)
          : (field.inputClasses || [])
      );
      return classes;
    },

    getPropertyValue(field: IDataTableFieldConfig, item: Record<string, any>): string {
      const fieldWithoutDRAlias = this.drAlias ? field.property.replace(`${this.drAlias}.`, '') : field.property;

      if (!fieldWithoutDRAlias.endsWith('?custom')) {
        const objectPath = fieldWithoutDRAlias.split('.');

        let value: any = item;

        objectPath.forEach((p: string) => {
          value = value ? value[p] : '';
        });

        if (value !== 0 && value) {
          return value;
        }
      }

      if (field.valueIfPropertyValueIsNull) {
        return field.valueIfPropertyValueIsNull(item);
      }

      if (fieldWithoutDRAlias.replace('?custom', '') === 'category' && item.category === null) {
        return 'N/A';
      }
      return '';
    },

    getSubTextValue(field: IDataTableFieldConfig, item: Record<string, any>): string {
      if (field.hasSubText) {
        const objectPath: string[] = field.subTextValue?.split('.') || [];
        let value = '';
        const obj = { ...item };

        objectPath.forEach((p: string) => {
          if (obj[p] && obj[p] !== undefined) {
            value = value.concat(`${obj[p]} `);
          }
        });

        return value;
      }
      return '';
    },

    getHref(item: Record<string, any>, field: IDataTableFieldConfig): string {
      if (field.dynamicHref) {
        return field.dynamicHref(item);
      }
      return (field.href || '').replace(':param', item.id);
    },

    getDropdownOptions(item: Record<string, any>) {
      const field: IDataTableFieldConfig = this.fields.find((f: IDataTableFieldConfig) => f.variant === 'dropdown')!;
      return field ? field.dropdownOptions!(item) : this.dropdownItems.map((d: IDropdownOptions) => ({ ...d, ...{ data: item } }));
    },

    handleItemClicked(item: any, field: any, index: number) {
      this.$emit(eventsMap.onItemClicked, item, field, index);
    },

    handleDeleteClicked(item: Record<string, any>, index: number) {
      this.$emit(eventsMap.onDeleteClicked, item, index);
    },

    handleRowSelect(item: any, field: IDataTableFieldConfig, index: number) {
      if (field.excludeFromRowClick) {
        return;
      }

      if (this.rowSelectOpensDetailsView) {
        this.handleDetailsViewToggle(index, item);
        return;
      }

      const variant = typeof field.variant === 'function' ? field.variant(item) : field.variant;

      if (variant && this.rowSelectable && rowSelectableVariants.includes(variant)) {
        this.$emit(eventsMap.onRowSelected, item, index);
      }
    },

    handleBannerAction() {
      this.$emit(eventsMap.onActionButtonPressed);
    },
    /**
     * Checks if a data item is disabled for checkbox selection
     * @param {IGenericObject} item - The data item to evaluate
     * @returns {boolean} Returns `true` if the item is disabled, `false` otherwise
    */
    isItemDisabled(item: IGenericObject): boolean {
      if (!this.disableCheckboxFunction) {
        return false;
      }
      return this.disableCheckboxFunction(item);
    },
    /**
     * Updates checked items based on the select-all checkbox state and emits an event
    */
    handleAllItemsChecked(): void {
      this.checkedItems = this.checkboxAll ? this.checkedItems.concat(this.dataList) : [];
      this.checkedItems = this.checkedItems?.filter((currentItem) => !this.isItemDisabled(currentItem));
      this.$emit(
        eventsMap.onCheckedItemsChanged,
        this.checkedItems,
      );
    },
    /**
     * Updates checked items based on a checkbox change and emits an event
     * @param {boolean} value - The new checkbox state (checked or unchecked)
     * @param {Record<string, string | number | Object | Date>} item - The data item associated with the checkbox
    */
    handleCheckboxChange(value: boolean, item: Record<string, string | number | Object | Date>): void {
      this.checkedItems = value
        ? [ ...this.checkedItems, item ]
        : this.checkedItems.filter((val) => val[this.checkboxBoundDataProperty] !== item[this.checkboxBoundDataProperty]);
      this.checkedItems = this.checkedItems?.filter((currentItem) => !this.isItemDisabled(currentItem));
      const allEnabledItems = this.dataList?.filter((currentItem) => !this.isItemDisabled(currentItem));
      this.checkboxAll = this.checkedItems.length === allEnabledItems?.length;
      this.$emit(
        eventsMap.onCheckedItemsChanged,
        this.checkedItems,
      );
    },

    handleDataFiltersChanged() {
      this.emitPayload = this.createFilters();
    },
    /**
     * Updates data filters based on pagination changes
    */
    handlePaginationChanged(): void {
      if (`${this.pagination?.currentPage}`) {
        this.dataFilters.page = `${this.pagination?.currentPage}`;
      }

      if (this.dataFilters.limit !== `${this.pagination?.recordsPerPage}`) {
        this.dataFilters.limit = `${this.pagination?.recordsPerPage}`;
      }
    },
    /**
      * Resets details view and checkbox state, then emits a pagination change event
    */
    handlePaginationEmit(): void {
      this.detailsViewIndexes = [];
      this.checkboxAll = false;
      this.$emit(eventsMap.onPaginationChanged, this.pagination);
    },
    /**
     * Updates sorting filters based on a fields sortability and emits a sorting change event
     * @param {IDataTableFieldConfig} field - The field configuration object
    */
    handleSorting(field: IDataTableFieldConfig): void {
      if (field.isSortable) {
        if (this.dataFilters.orderBy === field.property.replace('?custom', '')) {
          this.dataFilters.orderDir = this.dataFilters.orderDir === 'asc' ? 'desc' : 'asc';
        } else {
          this.dataFilters.orderBy = field.property.replace('?custom', '');
          this.dataFilters.orderDir = 'asc';
        }
        this.$emit(eventsMap.onSortingChanged, this.dataFilters.orderBy, this.dataFilters.orderDir);
      }
    },
    /**
     * Prepares export configuration with filters and fields, then emits an export event
    */
    handleExport(): void {
      const queryCommands: IQueryCommandOptions = this.createFilters().filters;

      if (this.exportConfig?.hasDateRangeModal) {
        queryCommands.where = (queryCommands.where ?? []).filter(
          (w) => (w as IWhereCommand).field !== this.exportConfig.dateRangeFilterProperty,
        );

        queryCommands.where.push(GreaterThan(this.exportConfig.dateRangeFilterProperty, dayjs(this.exportDateMin).subtract(1, 'day').format('YYYY-MM-DD')));
        queryCommands.where.push(LessThan(this.exportConfig.dateRangeFilterProperty, dayjs(this.exportDateMax).add(1, 'day').format('YYYY-MM-DD')));
      }

      this.$emit(eventsMap.onExportClicked, {
        fields: this.fields.filter((f: IDataTableFieldConfig) => !f.hideFromExport).map((f: IDataTableFieldConfig) => ({
          property: f.property.replace('?custom', ''),
          display: f.display,
          trueValue: f.trueValue || null,
          falseValue: f.falseValue || null,
          csvCustomTemplate: f.csvCustomTemplate || null,
        }) as IBaseFieldConfig),
        filters: {
          ...this.drAlias && { alias: this.drAlias },
          ...queryCommands,
          ...this.dataMeta?.totalRecords && { limit: this.dataMeta.totalRecords, page: 1 },
        },
        ...this.exportConfig?.hasDateRangeModal && {
          dateRange: `${this.exportDateMin}_to_${this.exportDateMax}`,
        },
      } as unknown as IExportConfig);

      this.isExportModalOpen = false;
    },

    handleFavoriteChange(event: Event, item: any) {
      this.$rsGlobals.$rsSwal.warning({
        title: item.featured ? 'Featured Property' : 'Turn Off Featured Property',
        text: item.featured ? 'Are you sure you would like to feature this property on your website?' : 'Are you sure you want to remove this property from the featured section of your website?',
        showDenyButton: true,
        confirmHandler: () => {
          this.$emit(eventsMap.onFavoriteClicked, item);
        },
        rejectHandler: () => {
          const el: HTMLInputElement | null = document.querySelector(`#switch_data_table_favorite_${item.id}`);
          if (!el) return;
          el.checked = !el.checked;
        },
      });
    },

    handleSwitchChanged(field: IDataTableFieldConfig, item: any) {
      const eventName = `on-${(field.property.replace('?custom', ''))}-changed`;
      this.$emit(eventName, item);
    },

    handleRadioClicked(field: IDataTableFieldConfig, item: Record<string, any>, itemIndex: number) {
      this.$emit(eventsMap.onRadioChanged, item, field.trueValue);
    },

    handleRadioAllClicked(field: IDataTableFieldConfig) {
      this.$emit(eventsMap.onRadioAllChanged, field, this.radioAll);
    },

    onInputChanged(item: string, itemIndex: number, fieldName: IDataTableFieldConfig, newValue: string) {
      this.$emit(eventsMap.onInputChanged, item, itemIndex, fieldName, newValue);
    },

    async handleDetailsViewToggle(index: number, item: any) {
      if (this.isIndexSelected(index)) {
        this.detailsViewIndexes = this.detailsViewIndexes.filter((d) => d !== index);
        this.$emit(eventsMap.onDetailsViewClosed, { index, item });
      } else {
        this.detailsViewIndexes = this.detailsViewType === 'accordion' ? [index] : [ ...this.detailsViewIndexes, index ];
        this.$emit(eventsMap.onDetailsViewOpened, { index, item });
      }
    },

    async handleDetailsViewOpenCloseAll(index: number, item: Record<string, any>, type: string) {
      if (type === 'close') {
        this.detailsViewIndexes = this.detailsViewIndexes.filter((d) => d !== index);
        this.$emit(eventsMap.onDetailsViewClosed, { index, item });
      } else {
        this.detailsViewIndexes = this.detailsViewType === 'accordion' ? [index] : [ ...this.detailsViewIndexes, index ];
        this.$emit(eventsMap.onDetailsViewOpened, { index, item });
      }
    },

    isIndexSelected(index: number) {
      return this.detailsViewIndexes.includes(index);
    },
    /**
     * Creates query filters and updates data filters based on field configurations and search criteria
     * @returns {{ filters: IQueryCommandOptions, query: Record<string, string | number | undefined> }} Returns an object containing query filters and the current data filters
    */
    createFilters(): { filters: IQueryCommandOptions, query: Record<string, string | number | undefined> } {
      const filters: IQueryCommandOptions = {
        ...this.drAlias && { alias: this.drAlias },
        limit: parseInt(this.dataFilters.limit!, 10),
        page: parseInt(this.dataFilters.page!, 10),
        ...(this.dataFilters.orderBy && {
          orderBy: [
            {
              field: this.dataFilters.orderBy,
              direction: this.dataFilters.orderDir || 'asc',
            },
          ],
        }),
      };
      const hasSearch = (this.fields as IDataTableFieldConfig[]).some((f) => f.isSearchable);

      const nestedFilter = hasSearch
        ? ({
          condition: 'and',
          whereCommands: [],
        } as INestedWhereCommand)
        : undefined;
      if (nestedFilter) {
        this.fields.forEach((f: IDataTableFieldConfig, idx: number) => {
          if (f.isSearchable) {
            nestedFilter.whereCommands.push({
              field: f.property.replace('?custom', ''),
              operator: 'like',
              condition: 'or',
              value: this.dataFilters.searchValue || '',
              notOperator: false,
            });
          }
        });

        filters.where = [
          ...(filters.where ? filters.where : []),
          ...[nestedFilter],
        ];
      }
      this.customFilters.forEach((c: IDataTableCustomFilter<IWhereCommand>) => {
        if (c.value) {
          filters.where = [
            ...(filters.where ? filters.where : []),
            ...[ { ...c.command, value: c.value }],
          ];
        }

        if (this.dataFilters[c.property.replace('?custom', '')] !== c.value) {
          this.dataFilters.page = '1';
        }

        this.dataFilters[c.property.replace('?custom', '')] = c.value;
      });

      if (
        this.fields.some((f) => f.isSearchable)
        && this.oldSearchValue !== this.dataFilters.searchValue
      ) {
        this.dataFilters.page = '1';
        this.oldSearchValue = this.dataFilters.searchValue as string;
      }

      if (this.oldLimit !== this.dataFilters.limit) {
        this.dataFilters.page = '1';
        this.oldLimit = this.dataFilters.limit;
      }

      const query = this.dataFilters;

      if (!hasSearch) {
        delete query.searchValue;
      }
      return { filters, query: this.dataFilters };
    },
    /**
     * Appends or updates a custom filter in the customFilters array
     * @param {IDataTableCustomFilter<IWhereCommand>} filter - The custom filter to append or update
    */
    appendCustomFilter(filter: IDataTableCustomFilter<IWhereCommand>): void {
      const idx = this.customFilters.findIndex(
        (c: IDataTableCustomFilter<IWhereCommand>) => c.customFilterId === filter.customFilterId,
      );

      if (idx !== -1) {
        this.customFilters[idx] = filter;
      } else {
        this.customFilters.push(filter);
      }
    },
    // Removes custom filter based on set customFilterId
    removeCustomFilter(customFilterId: string) {
      const temp = this.customFilters.filter((item) => item.customFilterId !== customFilterId);
      this.customFilters = temp;
    },
    /**
     * Triggers a data filter update and emits a filter change event if precise watch is enabled
    */
    triggerDataFilterUpdate(): void {
      this.isInited = true;
      this.handleDataFiltersChanged();
      if (this.usePreciseWatch) {
        this.$emit(eventsMap.onFiltersChanged, this.emitPayload);
      }
    },

    triggerDetailsClose() {
      this.detailsViewIndexes = [];
    },

    toggleReorderMode() {
      this.isReorderMode = !this.isReorderMode;
      if (this.isReorderMode) {
        this.detailsViewIndexes = [];
      }
    },

    triggerDetailsOpen() {
      this.detailsViewIndexes = [ ...this.detailsViewIndexes, this.openDetailsByIndex ];
    },

    updatePreviousSelect(value: string) {
      this.previousSelect = value;
    },

    /**
     * Hides Opened Dropdowns (besides the target one) by calling setInvisible method
     * @param dropdown Element Name
     */
    hideOtherDropdowns(dropdown: Element) {
      (this.$refs.dropdownParent as InstanceType<typeof RsDropdownActionMenu>[]).forEach((dropdownMenu) => {
        if (dropdownMenu.$el !== dropdown) dropdownMenu.setInvisible();
      });
    },

    handleSearch(value: string) {
      this.$emit(eventsMap.onSearch, value);
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables.scss";

#show-more-button {
  height: 40px;
}

.draggable-row {
  transition: cursor 0.15s;

  // Chevron fades out when entering reorder mode
  .details-view button {
    transition: opacity 0.2s ease;
    opacity: 1;
    pointer-events: auto;

    i {
      transition: opacity 0.2s ease, transform 0.2s ease;
    }
  }

  // Grip fades in when entering reorder mode
  .drag-grip {
    width: 28px;
    min-width: 28px;
    opacity: 0;
    transition: opacity 0.2s ease;
    color: $light-gray;
  }

  &.reorder-mode {
    cursor: grab !important;

    * {
      cursor: grab !important;
    }

    &:active,
    &:active * {
      cursor: grabbing !important;
    }

    .details-view button {
      opacity: 0;
      pointer-events: none;
    }

    .drag-grip {
      opacity: 1;
    }
  }
}

.reorder-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid $tertiary-gray;
  border-radius: 4px;
  background: transparent;
  color: $tertiary-medium-purple;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: $secondary-light;
    color: $secondary-color;
    border-color: $secondary-color;
  }

  &--active {
    background: $secondary-light;
    border-color: $secondary-color;
    color: $secondary-color;
  }
}
</style>
